// Painted text boxes (TT-1269): the sun, the scroll and the open book
// are watercolour art rather than path data, stored in the art catalog by
// platform `vt container-art --export` and served in its text-box manifest
// (TT-1334). The manifest cuts each piece into bands — `cols` / `rows` over
// the alpha bbox, each `fixed` (rolls, curls, covers, the gutter, page tops
// and feet) or `stretch` — and names the `inner` zone the lettering sits
// on. `placeArt` maps a text rect onto that zone: a uniform scale `k` sets
// the fixed bands' size, the stretch bands take up the rest, and a piece
// with no bands (the sun) scales uniformly round its zone. The result is a
// list of tiles, each a crop of the image drawn at a size, which `mountArt`
// draws as nested <svg viewBox> crops. Pure geometry here; the one DOM
// helper takes `el`.

import { NO_REACH, motifReach } from './motifs.js';

// how far a bandless piece may be squashed on one axis to fit the text
const MAX_ANISO = 1.6;

/** The manifest's bands for one axis, in image pixels. */
function bands(list, size, lo, hi) {
  if (!list?.length) return [[lo, hi, 'fixed']];
  return list.map(([a, b, kind]) => [a * size, b * size, kind]);
}

/**
 * Size the bands along one axis so the inner zone [i0, i1] (image px) maps
 * onto `target` units: fixed bands at `k`, stretch bands sharing the rest.
 * Returns [{ s0, s1, out }] (image px span, output size) plus the output
 * position of i0 relative to the first band's start.
 */
function sizeAxis(list, k, i0, i1, target) {
  const inner = list.filter(([a, b]) => b > i0 && a < i1);
  const stretchIn = inner.filter((b) => b[2] === 'stretch').reduce((s, [a, b]) => s + (Math.min(b, i1) - Math.max(a, i0)), 0);
  const fixedIn = inner.filter((b) => b[2] !== 'stretch').reduce((s, [a, b]) => s + (Math.min(b, i1) - Math.max(a, i0)), 0);
  // the stretch bands' scale: whatever fills `target` once the fixed parts are at k
  const ks = stretchIn > 0 ? Math.max(k, (target - fixedIn * k) / stretchIn) : k;
  const scale = (band) => (band[2] === 'stretch' ? ks : k);
  const sized = list.map((b) => ({ s0: b[0], s1: b[1], out: (b[1] - b[0]) * scale(b) }));
  // where i0 lands: walk the bands up to it
  let at = 0;
  for (const b of list) {
    if (b[1] <= i0) { at += (b[1] - b[0]) * scale(b); continue; }
    if (b[0] < i0) at += (i0 - b[0]) * scale(b);
    break;
  }
  return { sized, innerAt: at };
}

/**
 * Tiles for `asset` drawn so its inner zone covers `rect`: [{ sx, sy, sw,
 * sh, x, y, w, h }] in image px / rect units, plus the piece's reach
 * outside the rect.
 */
export function placeArt(asset, rect) {
  const { width: W, height: H } = asset;
  const [bx0, by0, bx1, by1] = asset.bbox.map((v, i) => v * (i % 2 ? H : W));
  const [ix0, iy0, ix1, iy1] = asset.inner.map((v, i) => v * (i % 2 ? H : W));
  const cols = bands(asset.cols, W, bx0, bx1);
  const rows = bands(asset.rows, H, by0, by1);
  const stretches = cols.some((b) => b[2] === 'stretch') || rows.some((b) => b[2] === 'stretch');
  // the uniform scale is the tighter fit when the piece can stretch to make
  // up the other axis. A piece with no bands (the sun) must hold the text
  // on both axes, so it takes the looser fit — squashed up to MAX_ANISO
  // along the other, which rays tolerate and a circle just about does
  const kx = rect.w / (ix1 - ix0);
  const ky = rect.h / (iy1 - iy0);
  const k = stretches ? Math.min(kx, ky) : Math.max(kx, ky);
  const kX = stretches ? k : Math.max(kx, ky / MAX_ANISO);
  const kY = stretches ? k : Math.max(ky, kx / MAX_ANISO);
  const X = sizeAxis(cols, kX, ix0, ix1, rect.w);
  const Y = sizeAxis(rows, kY, iy0, iy1, rect.h);
  // the piece's origin, so the zone's corner lands on the rect's; a piece
  // that cannot stretch centres its (larger) zone on the rect instead
  const zoneW = (ix1 - ix0) * kX;
  const zoneH = (iy1 - iy0) * kY;
  const ox = rect.x - X.innerAt - (stretches ? 0 : (zoneW - rect.w) / 2);
  const oy = rect.y - Y.innerAt - (stretches ? 0 : (zoneH - rect.h) / 2);
  const tiles = [];
  let y = oy;
  for (const r of Y.sized) {
    let x = ox;
    for (const c of X.sized) {
      tiles.push({ sx: c.s0, sy: r.s0, sw: c.s1 - c.s0, sh: r.s1 - r.s0, x, y, w: c.out, h: r.out });
      x += c.out;
    }
    y += r.out;
  }
  const wAll = X.sized.reduce((s, c) => s + c.out, 0);
  const hAll = Y.sized.reduce((s, r) => s + r.out, 0);
  const reach = {
    left: Math.max(0, rect.x - ox),
    top: Math.max(0, rect.y - oy),
    right: Math.max(0, ox + wAll - rect.x - rect.w),
    bottom: Math.max(0, oy + hAll - rect.y - rect.h),
  };
  return { tiles, reach };
}

/** The piece's reach outside `rect` (see placeArt), for layout. */
export function artReach(asset, rect) {
  return placeArt(asset, rect).reach;
}

/**
 * Draw the tiles under `g`: one nested <svg> per tile, its viewBox the crop,
 * stretched to the tile's size. `el(tag, attrs, parent)` is the caller's
 * element helper (render.js).
 */
export function mountArt(g, asset, rect, el) {
  const { tiles } = placeArt(asset, rect);
  for (const t of tiles) {
    const s = el('svg', { x: t.x, y: t.y, width: t.w, height: t.h, viewBox: `${t.sx} ${t.sy} ${t.sw} ${t.sh}`, preserveAspectRatio: 'none' }, g);
    el('image', { href: asset.url, x: 0, y: 0, width: asset.width, height: asset.height, preserveAspectRatio: 'none' }, s);
  }
}

/**
 * A text box's paint outside its text rect, for layout: the painted
 * piece's (`artReach`) when the host has the art for the intent, else the
 * drawn motif's (`motifReach`); none for a plain box, or a painted-only
 * intent without its art (TT-1333). Shared by the board's
 * packer and live solver and the scene's story solver (TT-1330).
 */
export function textBoxReach(intent, size, art) {
  if (!intent) return NO_REACH;
  const r = { x: 0, y: 0, w: size.w, h: size.h };
  return art?.[intent] ? artReach(art[intent], r) : motifReach(intent, r);
}
